import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { dispatchAPI, driverVerificationAPI } from "../services/api";

const Responses = ({ formType }) => {
   const [responses, setResponses] = useState([]);
   const [loading, setLoading] = useState(true);
   const [error, setError] = useState("");
   const [total, setTotal] = useState(0);

   useEffect(() => {
      fetchResponses();
   }, [formType]);

   const fetchResponses = async () => {
      try {
         setLoading(true);
         let data;

         if (formType === "dispatch") {
            data = await dispatchAPI.getResponses();
         } else if (formType === "driver_verification") {
            data = await driverVerificationAPI.getResponses();
         }

         setResponses(data.responses || []);
         setTotal(data.total || 0);
      } catch (err) {
         console.error("Error fetching responses:", err);
         setError("Failed to load responses");
      } finally {
         setLoading(false);
      }
   };

   const handleClearResponses = async () => {
      if (
         !window.confirm(
            "Are you sure you want to clear all responses? This action cannot be undone."
         )
      ) {
         return;
      }

      try {
         if (formType === "dispatch") {
            await dispatchAPI.clearResponses();
         } else if (formType === "driver_verification") {
            await driverVerificationAPI.clearResponses();
         }

         fetchResponses(); // Refresh data
      } catch (err) {
         console.error("Error clearing responses:", err);
         alert("Failed to clear responses");
      }
   };

   const getFormTitle = () => {
      if (formType === "dispatch") {
         return "Dispatch Information Responses";
      } else if (formType === "driver_verification") {
         return "Driver Verification Responses";
      }
      return "Form Responses";
   };

   // Helper to render a badge for categorical values
   const renderBadge = (value, type = "info") => {
      if (!value) return null;
      let badgeClass = "badge-info";
      let display = value;
      switch (type) {
         case "driverType":
            if (value === "Solo") badgeClass = "badge-primary";
            else if (value === "Team") badgeClass = "badge-warning";
            else badgeClass = "badge-info";
            display = value;
            break;
         case "equipmentType":
            if (value === "Dry Van") badgeClass = "badge-primary";
            else if (value === "Reefer") badgeClass = "badge-info";
            else if (value === "Flatbed") badgeClass = "badge-warning";
            else if (value === "Step Deck") badgeClass = "badge-success";
            else if (value === "Lowboy") badgeClass = "badge-danger";
            else badgeClass = "badge-info";
            display = value;
            break;
         case "emptyStatus":
            if (value === "Yes" || value === "Empty") badgeClass = "badge-success";
            else if (value === "No" || value === "Loaded") badgeClass = "badge-danger";
            else badgeClass = "badge-info";
            display = value;
            break;
         default:
            badgeClass = "badge-info";
            display = value;
      }
      return <span className={`badge-custom ${badgeClass}`}>{display}</span>;
   };

   const renderResponseData = (response) => {
      if (formType === "dispatch") {
         return (
            <div className="response-fields">
               <div>
                  <span className="info-label">Dispatcher:</span>{" "}
                  <span className="info-value">
                     {response["Dispatcher First Name"]} {response["Dispatcher Last Name"]}
                  </span>
               </div>
               <div>
                  <span className="info-label">Driver:</span>{" "}
                  <span className="info-value">
                     {response["Driver First Name"]} {response["Driver Last Name"]}
                  </span>
               </div>
               <div>
                  <span className="info-label">Driver Type:</span>{" "}
                  {renderBadge(response["Driver Type"], "driverType")}
               </div>
               <div>
                  <span className="info-label">Equipment:</span>{" "}
                  {renderBadge(response["Equipment Type"], "equipmentType")}
               </div>
               <div>
                  <span className="info-label">Location:</span>{" "}
                  <span className="info-value">{response["Driver Location"]}</span>
               </div>
               <div>
                  <span className="info-label">Empty Status:</span>{" "}
                  {renderBadge(response["Empty Status"], "emptyStatus")}
               </div>
               <div>
                  <span className="info-label">Submitted:</span>{" "}
                  <span className="info-value">
                     {response.timestamp ? new Date(response.timestamp).toLocaleString() : ""}
                  </span>
               </div>
            </div>
         );
      } else if (formType === "driver_verification") {
         return (
            <div className="response-fields">
               <div>
                  <span className="info-label">Driver:</span>{" "}
                  <span className="info-value">
                     {response["Driver First Name"]} {response["Driver Last Name"]}
                  </span>
               </div>
               <div>
                  <span className="info-label">Phone:</span>{" "}
                  <span className="info-value">{response["Driver Phone"]}</span>
               </div>
               <div>
                  <span className="info-label">Location:</span>{" "}
                  <span className="info-value">{response["Location"]}</span>
               </div>
               <div>
                  <span className="info-label">Empty Status:</span>{" "}
                  {renderBadge(response["Empty Status"], "emptyStatus")}
               </div>
               <div>
                  <span className="info-label">Submitted:</span>{" "}
                  <span className="info-value">
                     {response.timestamp ? new Date(response.timestamp).toLocaleString() : ""}
                  </span>
               </div>
            </div>
         );
      }
      return null;
   };

   if (loading) {
      return (
         <div className="loading-container">
            <div className="loading-spinner"></div>
         </div>
      );
   }

   return (
      <div className="form-container" style={{ maxWidth: "1200px" }}>
         <div className="form-header">
            <h1>{getFormTitle()}</h1>
            <p>Total Responses: {total}</p>
         </div>

         {error && <div className="alert alert-danger">{error}</div>}

         {/* Actions */}
         <div className="card">
            <div
               style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "16px",
               }}
            >
               <div style={{ display: "flex", gap: "16px" }}>
                  <Link
                     to="/dashboard"
                     className="btn-secondary"
                     style={{ textDecoration: "none", width: "auto" }}
                  >
                     Back to Dashboard
                  </Link>
                  <Link
                     to="/forms"
                     className="btn-secondary"
                     style={{ textDecoration: "none", width: "auto" }}
                  >
                     Forms Dashboard
                  </Link>
               </div>
               {responses.length > 0 && (
                  <button
                     onClick={handleClearResponses}
                     className="btn-danger"
                     style={{ width: "auto" }}
                  >
                     Clear All Responses
                  </button>
               )}
            </div>
         </div>

         {/* Responses */}
         {responses.length === 0 ? (
            <div className="card">
               <p className="text-center text-muted">No responses yet.</p>
            </div>
         ) : (
            <div className="card-grid">
               {responses.map((response, index) => (
                  <div key={index} className="card response-card">
                     <div className="card-header">
                        <h3 className="card-title" style={{ fontSize: "1rem" }}>
                           Response #{responses.length - index}
                        </h3>
                        {response.frontConversation && (
                           <span className="badge-custom badge-primary">
                              {response.frontConversation}
                           </span>
                        )}
                     </div>
                     {renderResponseData(response)}
                     <details style={{ marginTop: "16px" }}>
                        <summary
                           style={{
                              cursor: "pointer",
                              color: "var(--primary)",
                              fontWeight: "500",
                           }}
                        >
                           View Full Response
                        </summary>
                        <pre
                           style={{
                              marginTop: "12px",
                              padding: "12px",
                              background: "var(--background)",
                              borderRadius: "var(--radius-sm)",
                              fontSize: "0.875rem",
                              overflow: "auto",
                           }}
                        >
                           {JSON.stringify(response, null, 2)}
                        </pre>
                     </details>
                  </div>
               ))}
            </div>
         )}

         {/* Quick Actions */}
         <div className="card" style={{ marginTop: "40px" }}>
            <h2 className="card-title">Quick Actions</h2>
            <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
               {formType === "dispatch" ? (
                  <Link
                     to="/dispatch-form"
                     className="btn-primary"
                     style={{ textDecoration: "none", width: "auto", marginTop: 0 }}
                  >
                     Submit New Dispatch Form
                  </Link>
               ) : (
                  <Link
                     to="/driver-form"
                     className="btn-primary"
                     style={{ textDecoration: "none", width: "auto", marginTop: 0 }}
                  >
                     Submit New Driver Verification
                  </Link>
               )}
               <Link
                  to="/dashboard"
                  className="btn-secondary"
                  style={{ textDecoration: "none", width: "auto" }}
               >
                  View Dashboard
               </Link>
            </div>
         </div>
      </div>
   );
};

export default Responses;
